import React from "react";
import { useTranslation } from "react-i18next";
import { FlightBadgeState } from "./types";

export interface BadgePillProps {
  badge: FlightBadgeState;
  diffMinutes?: number;
}

// Figma spec (DASH-6077): early/on-time/late dots and text use the
// success/primary/pending token trio respectively. The other states
// (boarding, finalCall, gateClosed, cancelled, landed, departed,
// statusUnavailable) don't have Figma tokens yet — they keep their
// pre-Figma placeholder colors, flagged here for follow-up.
export const BADGE_TEXT_CLASS: Record<FlightBadgeState, string> = {
  onTime: "text-brand-blue", // TODO(DASH-6077): confirm real "primary/ink" hex
  early: "text-success-ink",
  delayed: "text-pending-ink",
  boarding: "text-flight-blue",
  finalCall: "text-flight-red",
  gateClosed: "text-flight-red",
  cancelled: "text-flight-red",
  landed: "text-flight-neutral",
  departed: "text-flight-neutral",
  statusUnavailable: "text-flight-neutral",
};

const BADGE_BG_CLASS: Record<FlightBadgeState, string> = {
  onTime: "bg-primary-tint",
  early: "bg-success-tint",
  delayed: "bg-pending-tint",
  boarding: "bg-flight-blue-bg",
  finalCall: "bg-flight-red-bg",
  gateClosed: "bg-flight-red-bg",
  cancelled: "bg-flight-red-bg",
  landed: "bg-flight-neutral-bg",
  departed: "bg-flight-neutral-bg",
  statusUnavailable: "bg-flight-neutral-bg",
};

const BADGE_DOT_CLASS: Record<FlightBadgeState, string> = {
  onTime: "bg-primary-100",
  early: "bg-success-100",
  delayed: "bg-pending-100",
  boarding: "bg-current",
  finalCall: "bg-current",
  gateClosed: "bg-current",
  cancelled: "bg-current",
  landed: "bg-current",
  departed: "bg-current",
  statusUnavailable: "bg-current",
};

const BADGE_LABEL_KEY: Record<FlightBadgeState, string> = {
  onTime: "results.badgeOnTime",
  early: "results.badgeEarly",
  delayed: "results.badgeDelayed",
  boarding: "results.badgeBoarding",
  finalCall: "results.badgeFinalCall",
  gateClosed: "results.badgeGateClosed",
  cancelled: "results.badgeCancelled",
  landed: "results.badgeLanded",
  departed: "results.badgeDeparted",
  statusUnavailable: "results.badgeStatusUnavailable",
};

export const BadgePill: React.FC<BadgePillProps> = ({ badge, diffMinutes }) => {
  const { t } = useTranslation("flightAlerts");
  const needsAmount = badge === "delayed" || badge === "early";
  const label = needsAmount
    ? t(BADGE_LABEL_KEY[badge], { minutes: Math.abs(diffMinutes ?? 0) })
    : t(BADGE_LABEL_KEY[badge]);

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-[14px] py-[7px] font-lexend text-[14px]/[18px] font-bold tracking-normal ${BADGE_BG_CLASS[badge]} ${BADGE_TEXT_CLASS[badge]}`}
    >
      <span className={`h-2 w-2 rounded-full ${BADGE_DOT_CLASS[badge]}`} />
      {label}
    </span>
  );
};
